import {
	FlowConfig,
	makeFlattenedFlowMappingsUnique,
	MicroTaskConfig,
	ObjectState,
	TaskMapping,
} from 'ihub/common';
import {
	createContext,
	Dispatch,
	ReactNode,
	SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import ValidationGroup from '../../classes/validation-group';
import InitTaskSampleData from '../../components/micro-task/init-task-sample-data';
import {
	advancedPanelName,
	generalPanelName,
	InputDataPanelName,
} from '../../components/micro-task/micro-task-constants';
import dispatchSnackbar from '../../functions/dispatch-snackbar';
import { getSubTaskMappings } from './helper';

const SESSION_TASKS_KEY = 'copiedTasks';
const SESSION_TASK_MAPPINGS = 'copiedMappings';
const MAX_CLIPBOARD_TASKS = 25;
/**
 * Represents the clipboard dictionary where each key is a flow name
 * and the value is the list of tasks copied from that flow.
 */
type CopiedTasksMap = Record<string, MicroTaskConfig[]>;

type TaskClipboardContextValue = {
	/** The list of task IDs currently selected for copying. */
	selectedTaskIds: string[];
	/**
	 * Dictionary of copied tasks grouped by flow name.
	 * Key: flowName, Value: array of tasks copied from that flow.
	 */
	copiedTasksMap: CopiedTasksMap;
	/** The list of task IDs selected for pasting/managing. */
	tasksToManageIds: string[];
	currentClipboardTotal: string;
	/**
	 * Copies tasks to the clipboard grouped by flow name.
	 * Prevents duplicate tasks from being added to the same flow group.
	 * @param microTasks - The full list of available micro tasks to search from.
	 * @param flowName - The name of the flow the tasks are being copied from.
	 * @param individualTaskId - If provided, copies only the task with this ID.
	 *                           Otherwise, copies all currently selected tasks.
	 * @param taskMappings - If provided, copies matching success/failure mappings for the copied tasks.
	 */
	copyTasks: (
		microTasks: MicroTaskConfig[],
		flowName: string,
		individualTaskId?: string,
		taskMappings?: TaskMapping[]
	) => void;
	/** Pastes the currently managed tasks into the active flow. */
	pasteTasks: (
		microTasks: MicroTaskConfig[],
		setFlow: Dispatch<SetStateAction<FlowConfig>>,
		setMicroTasks: Dispatch<SetStateAction<MicroTaskConfig[]>>,
		setValidationGroup: Dispatch<SetStateAction<ValidationGroup>>
	) => void;
	/**
	 * Toggles a task ID in the multi-select copy list.
	 * If the task is not selected it will be added, if already selected it will be removed.
	 * @param taskId - The ID of the task to toggle in the selection.
	 */
	addTaskToCopyList: (taskId: string) => void;
	/**
	 * Toggles a task ID in the manage/paste selection list.
	 * If the task is not selected it will be added, if already selected it will be removed.
	 * @param taskId - The ID of the task to toggle.
	 */
	toggleManageTaskSelected: (taskId: string) => void;
	/** Clears all task IDs from the manage selection list. */
	clearManagedTaskIds: () => void;
	/** Clears all task IDs from the copy selection list. */
	clearSelectedTasks: () => void;
	/**
	 * Removes tasks from the clipboard by their IDs across all flow groups.
	 * Empty flow groups are pruned automatically.
	 * Also removes the affected IDs from the manage selection list.
	 * @param flowName - If provided, removes all the tasks copied from the specific flow.
	 */
	removeTasksFromClipboard: (flowName?: string) => void;
	clearClipboard: () => void;
};

export const TaskClipboardContext = createContext<TaskClipboardContextValue>({
	selectedTaskIds: [],
	copiedTasksMap: {},
	tasksToManageIds: [],
	currentClipboardTotal: '',
	copyTasks: () => {
		/** */
	},
	pasteTasks: () => {
		/** */
	},
	addTaskToCopyList: () => {
		/** */
	},
	toggleManageTaskSelected: () => {
		/** */
	},
	clearManagedTaskIds: () => {
		/** */
	},
	clearSelectedTasks: () => {
		/** */
	},
	removeTasksFromClipboard: () => {
		/** */
	},
	clearClipboard: () => {
		/** */
	},
});

/**
 * Provides clipboard functionality for copying and pasting tasks within flows.
 * Tasks are stored in a dictionary grouped by flow name for easy differentiation.
 * State is persisted to sessionStorage so it survives navigation between routes.
 * @param children - The child components that will have access to the clipboard context.
 */
export default function TaskClipboardProvider({ children }: { children: ReactNode }) {
	const [copiedTasksMap, setCopiedTasksMap] = useState<CopiedTasksMap>({});
	const [copiedTaskMappings, setCopiedTaskMappings] = useState<TaskMapping[]>([]);
	const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
	const [tasksToManageIds, setTasksToManageIds] = useState<string[]>([]);

	/**00
	 * Rehydrates clipboard state from sessionStorage on mount,
	 * restoring previously copied tasks grouped by flow name and task mappings.
	 */
	useEffect(() => {
		const rehydrateState = () => {
			const storedTasks = sessionStorage.getItem(SESSION_TASKS_KEY);
			if (storedTasks) {
				const parsed = JSON.parse(storedTasks) as CopiedTasksMap;
				const rehydrated: CopiedTasksMap = {};
				for (const [flowName, tasks] of Object.entries(parsed)) {
					rehydrated[flowName] = tasks.map(t => new MicroTaskConfig(t));
				}
				setCopiedTasksMap(rehydrated);
			}
			const storedTaskMappings = sessionStorage.getItem(SESSION_TASK_MAPPINGS);
			if (storedTaskMappings) {
				const parsed = JSON.parse(storedTaskMappings) as TaskMapping[];
				setCopiedTaskMappings(parsed.map(m => new TaskMapping(m)));
			}
		};

		rehydrateState();
	}, []);

	/**
	 * Copies tasks to the clipboard dictionary grouped by flow name.
	 * Prevents duplicate tasks from being added to the same flow group.
	 * If taskMappings are provided, matching mappings for the copied tasks will also be stored.
	 * @param microTasks - The full list of available micro tasks to search from.
	 * @param flowName - The name of the flow the tasks are being copied from.
	 * @param individualTaskId - If provided, copies only the task with this ID.
	 *                           Otherwise, copies all currently selected tasks.
	 * @param taskMappings - If provided, filters and copies mappings for the copied tasks.
	 */
	const copyTasks = useCallback(
		(
			microTasks: MicroTaskConfig[],
			flowName: string,
			individualTaskId?: string,
			taskMappings?: TaskMapping[]
		) => {
			if (individualTaskId) {
				const taskToCopy = microTasks.find(x => x.id === individualTaskId);
				if (taskToCopy) {
					setCopiedTasksMap(prev => {
						const flowTasks = prev[flowName] ?? [];
						if (flowTasks.some(t => t.id === taskToCopy.id)) return prev;
						const total = Object.values(prev).reduce((sum, tasks) => sum + tasks.length, 0);
						if (total >= MAX_CLIPBOARD_TASKS) {
							dispatchSnackbar({
								message: `Please remove a task from the clipboard before adding a new task. The current limit for clipboard content is: ${MAX_CLIPBOARD_TASKS}`,
								severity: 'warning',
							});
							return prev;
						}
						const updated = {
							...prev,
							[flowName]: [...flowTasks, taskToCopy],
						};
						sessionStorage.setItem(SESSION_TASKS_KEY, JSON.stringify(updated));
						return updated;
					});

					if (taskMappings) {
						const mappingsToCopy = taskMappings.filter(m => m.sourceTaskID === taskToCopy.id);

						// Also collect sub-task mappings
						const subMappings = getSubTaskMappings(taskToCopy, taskMappings);

						setCopiedTaskMappings(prev => {
							const allMappings = [...mappingsToCopy, ...subMappings];
							const newMappings = allMappings.filter(m => !prev.some(p => p.id === m.id));
							if (newMappings.length === 0) return prev;
							const temp = [...prev, ...newMappings];
							sessionStorage.setItem(SESSION_TASK_MAPPINGS, JSON.stringify(temp));
							return temp;
						});
					}
				}
			} else {
				const tasksToCopy: MicroTaskConfig[] = [];
				const taskMappingsToCopy: TaskMapping[] = [];

				for (const selectedTaskId of selectedTaskIds) {
					const taskToCopy = microTasks.find(x => x.id === selectedTaskId);
					if (taskToCopy) {
						if (taskMappings) {
							// Collect mappings for the top-level task
							const mappingsToCopy = taskMappings.filter(m => m.sourceTaskID === taskToCopy.id);
							taskMappingsToCopy.push(...mappingsToCopy);

							// Collect mappings for any sub-tasks based on their taskType
							taskMappings.push(...getSubTaskMappings(taskToCopy, taskMappings));
						}
						tasksToCopy.push(taskToCopy);
					}
				}

				setCopiedTasksMap(prev => {
					const flowTasks = prev[flowName] ?? [];
					const newTasks = tasksToCopy.filter(t => !flowTasks.some(ft => ft.id === t.id));
					if (newTasks.length === 0) return prev;
					const total = Object.values(prev).reduce((sum, tasks) => sum + tasks.length, 0);
					const remaining = MAX_CLIPBOARD_TASKS - total;
					if (newTasks.length >= remaining) {
						dispatchSnackbar({
							message: `Please remove tasks from the clipboard before adding new tasks. The current limit for clipboard content is: ${MAX_CLIPBOARD_TASKS}`,
							severity: 'warning',
						});
						return prev;
					}
					const updated = { ...prev, [flowName]: [...flowTasks, ...newTasks] };
					sessionStorage.setItem(SESSION_TASKS_KEY, JSON.stringify(updated));
					return updated;
				});

				setCopiedTaskMappings(prev => {
					const newMappings = taskMappingsToCopy.filter(m => !prev.some(p => p.id === m.id));
					if (newMappings.length === 0) return prev;
					const temp = [...prev, ...newMappings];
					sessionStorage.setItem(SESSION_TASK_MAPPINGS, JSON.stringify(temp));
					return temp;
				});

				setSelectedTaskIds([]);
			}
		},
		[selectedTaskIds]
	);

	/**
	 * Removes tasks from the clipboard across all flow groups.
	 * Empty flow groups are pruned automatically after removal.
	 * Also removes the affected IDs from the manage selection list.
	 * @param flowName - If provided, removes the entire flow group and all its tasks.
	 *                   If omitted, removes only the tasks currently in `tasksToManageIds`.
	 */
	const removeTasksFromClipboard = useCallback(
		(flowName?: string) => {
			if (flowName) {
				setCopiedTasksMap(prev => {
					const updated = { ...prev };
					const removedIds = (updated[flowName] ?? []).map(t => t.id);
					delete updated[flowName];
					sessionStorage.setItem(SESSION_TASKS_KEY, JSON.stringify(updated));
					setTasksToManageIds(prev => prev.filter(id => !removedIds.includes(id)));
					return updated;
				});
			} else {
				const taskIds = [...tasksToManageIds];
				setCopiedTasksMap(prev => {
					const updated: CopiedTasksMap = {};
					for (const [name, tasks] of Object.entries(prev)) {
						const remaining = tasks.filter(t => !taskIds.includes(t.id));
						if (remaining.length > 0) updated[name] = remaining;
					}
					sessionStorage.setItem(SESSION_TASKS_KEY, JSON.stringify(updated));
					return updated;
				});
				setTasksToManageIds(prev => prev.filter(id => !taskIds.includes(id)));
			}
		},
		[tasksToManageIds]
	);

	/**
	 * Toggles a task ID in the multi-select copy list.
	 * If the task is not selected it will be added, if already selected it will be removed.
	 * @param taskId - The ID of the task to toggle in the selection.
	 */
	const addTaskToCopyList = useCallback((taskId: string) => {
		setSelectedTaskIds(prev =>
			prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]
		);
	}, []);

	/**
	 * Toggles a task ID in the multi-select manage list.
	 * If the task is not selected it will be added, if already selected it will be removed.
	 * @param taskId - The ID of the task to toggle.
	 */
	const toggleManageTaskSelected = useCallback((taskId: string) => {
		setTasksToManageIds(prev =>
			prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]
		);
	}, []);

	const currentClipboardTotal = useMemo(() => {
		const copiedAmount = Object.values(copiedTasksMap).reduce(
			(total, tasks) => total + tasks.length,
			0
		);
		return `${copiedAmount}/${MAX_CLIPBOARD_TASKS} Clipboard Tasks`;
	}, [copiedTasksMap]);

	const clearClipboard = useCallback(() => {
		setCopiedTasksMap({});
		setCopiedTaskMappings([]);
		setTasksToManageIds([]);
		setSelectedTaskIds([]);
		sessionStorage.removeItem(SESSION_TASKS_KEY);
		sessionStorage.removeItem(SESSION_TASK_MAPPINGS);
	}, []);

	const pasteTasks = useCallback(
		(
			microTasks: MicroTaskConfig[],
			setFlow: Dispatch<SetStateAction<FlowConfig>>,
			setMicroTasks: Dispatch<SetStateAction<MicroTaskConfig[]>>,
			setValidationGroup: Dispatch<SetStateAction<ValidationGroup>>
		) => {
			const allCopiedTasks = Object.values(copiedTasksMap).flat();
			const tasksToPaste = allCopiedTasks.filter(t => tasksToManageIds.includes(t.id));

			if (tasksToPaste.length === 0) {
				dispatchSnackbar({
					message: 'No tasks selected to paste.',
					severity: 'warning',
				});
				return;
			}

			// makeFlattenedFlowMappingsUnique expands sub-tasks and remaps their mappings
			const { taskMappings, subTaskConfigs } = makeFlattenedFlowMappingsUnique(
				copiedTaskMappings.filter(m => tasksToManageIds.includes(m.sourceTaskID)),
				tasksToPaste
			);

			const newMicroTaskConfigs: MicroTaskConfig[] = [];
			const accumulatedTasks = [...microTasks];
			const newValidationGroups: ValidationGroup[] = [];

			for (const task of subTaskConfigs) {
				try {
					const newMicroTaskConfig = new MicroTaskConfig({
						name: newMicroTaskName(task.name, accumulatedTasks),
						taskType: task.taskType,
						config: JSON.parse(JSON.stringify(task.config)),
						objectState: ObjectState.Created,
						taskDisabled: task.taskDisabled,
					});
					newMicroTaskConfig.id = task.id;

					accumulatedTasks.push(newMicroTaskConfig);

					newValidationGroups.push(
						new ValidationGroup({
							id: newMicroTaskConfig.referenceId,
							label: newMicroTaskConfig.name,
							validationItems: [
								new ValidationGroup({
									id: `${newMicroTaskConfig.referenceId}_${generalPanelName}`,
								}),
								new ValidationGroup({
									id: `${newMicroTaskConfig.referenceId}_${InputDataPanelName}`,
								}),
								new ValidationGroup({
									id: `${newMicroTaskConfig.referenceId}_${advancedPanelName}`,
								}),
							],
						})
					);

					InitTaskSampleData(newMicroTaskConfig);
					newMicroTaskConfigs.push(newMicroTaskConfig);
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
				} catch (e) {
					dispatchSnackbar({
						message: `Failed to paste task: ${task.name}`,
						severity: 'error',
					});
				}
			}

			if (newMicroTaskConfigs.length > 0) {
				setValidationGroup(prev => {
					const temp = new ValidationGroup(prev);
					temp.validationItems.push(...newValidationGroups);
					return temp;
				});

				setFlow(prevState => {
					const temp = new FlowConfig(JSON.parse(JSON.stringify(prevState)));
					temp.taskMappings.push(...taskMappings);
					return temp;
				});

				setMicroTasks(prevState => [...prevState, ...newMicroTaskConfigs]);
			}
		},
		[copiedTasksMap, tasksToManageIds, copiedTaskMappings]
	);

	const contextValue = useMemo<TaskClipboardContextValue>(
		() => ({
			selectedTaskIds,
			copiedTasksMap,
			tasksToManageIds,
			currentClipboardTotal,
			addTaskToCopyList,
			copyTasks,
			toggleManageTaskSelected,
			removeTasksFromClipboard,
			pasteTasks,
			clearManagedTaskIds: () => setTasksToManageIds([]),
			clearSelectedTasks: () => setSelectedTaskIds([]),
			clearClipboard,
		}),
		[
			selectedTaskIds,
			copiedTasksMap,
			tasksToManageIds,
			addTaskToCopyList,
			copyTasks,
			toggleManageTaskSelected,
			removeTasksFromClipboard,
			clearClipboard,
			pasteTasks,
			currentClipboardTotal,
		]
	);

	return (
		<TaskClipboardContext.Provider value={contextValue}>{children}</TaskClipboardContext.Provider>
	);
}

/**
 * Hook to access the TaskClipboard context.
 * Must be used within a {@link TaskClipboardProvider}.
 * @throws If used outside of a `TaskClipboardProvider`.
 * @returns The current TaskClipboard context value.
 */
export const useTaskClipboardContext = () => {
	const context = useContext(TaskClipboardContext);
	if (!context) {
		throw new Error('useTaskClipboardContext must be used within a TaskClipboardProvider');
	}
	return context;
};

function newMicroTaskName(name: string, oldMicroTasks: MicroTaskConfig[]): string {
	let joinedName = `${name}`;
	if (/ \d+$/.test(name)) {
		const splitName = name.split(' ');
		const lastNum = splitName.pop();
		if (lastNum) {
			joinedName = splitName.concat((Number(lastNum) + 1).toString()).join(' ');
		}
	} else {
		joinedName = `${name} 1`;
	}
	oldMicroTasks.forEach(microTask => {
		if (microTask.name === joinedName) {
			joinedName = newMicroTaskName(joinedName, oldMicroTasks);
		}
	});
	return joinedName;
}
